// 1. Optimized ResumeSelector Component
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';

const ResumeSelector = ({ onSelect, selectedResume }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the selected value to avoid unnecessary re-renders
  const selectedValue = useMemo(() => {
    return selectedResume?.id || "";
  }, [selectedResume?.id]);

  // Memoize the fetch function to prevent recreating on every render
  const fetchResumes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/api/resumes');
      setResumes(data);
      
      // Auto-select first resume only if no selection exists and we have data
      if (data.length > 0 && !selectedResume) {
        const firstResume = data[0];
        onSelect({
          id: firstResume.id,
          name: firstResume.name
        });
      }
    } catch (err) {
      setError('Failed to load resumes');
      console.error('Resume fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [onSelect, selectedResume]);

  useEffect(() => {
    fetchResumes();
  }, [fetchResumes]);

  const handleResumeChange = useCallback((e) => {
    const resumeId = e.target.value;
    
    if (resumeId === "") {
      onSelect(null);
      return;
    }
    
    const resume = resumes.find(r => r.id === resumeId);
    if (resume) {
      onSelect({
        id: resume.id,
        name: resume.name
      });
    }
  }, [resumes, onSelect]);

  // Memoize resume options to prevent recreation on every render
  const resumeOptions = useMemo(() => 
    resumes.map(resume => (
      <option key={resume.id} value={resume.id}>
        {resume.name}
      </option>
    )), [resumes]
  );

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span>Loading resumes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-2 bg-red-50 rounded border border-red-200">
        {error}
        <button 
          onClick={fetchResumes}
          className="ml-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (resumes.length === 0) {
    return (
      <div className="text-gray-500 p-2 bg-gray-50 rounded border border-gray-200">
        No resumes found. Please upload a resume first.
      </div>
    );
  }

  return (
    <div className="resume-selector">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Select Resume
      </label>
      <select
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        value={selectedValue}
        onChange={handleResumeChange}
      >
        <option value="">Select a resume</option>
        {resumeOptions}
      </select>
      <div className="mt-2 text-xs text-gray-500">
        This resume will be used for job recommendations and applications.
      </div>
    </div>
  );
};

export default ResumeSelector;